/**
 * Retour d'action commun aux écrans (Snackbar + Alert MUI), monté une seule
 * fois : les écrans appellent `useNotify` au lieu de porter chacun un état
 * de Snackbar et son JSX (ré-audit DRY, 39 copies).
 *
 * Chaque écran choisit un préréglage nommé (`NOTIFY_PRESETS`, ré-audit
 * N-17) qui garde son rendu d'origine (durée, position, variante, bouton de
 * fermeture, largeur). `notifyError` affiche le message renvoyé par le
 * backend : l'utilisateur voit pourquoi l'action n'a pas abouti (audit
 * console DT-1, DR §2.3).
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, AlertColor, AlertProps, Snackbar, SnackbarOrigin } from '@mui/material';
import { toUserMessage } from '../lib/http';
import { logger } from '../lib/logger';

/** Rendu du message. */
interface NotifyOptions {
  /** Durée d'affichage (ms). */
  autoHideMs?: number;
  /** Position du Snackbar (défaut MUI : en bas à gauche). */
  anchorOrigin?: SnackbarOrigin;
  /** Variante de l'Alert. */
  variant?: AlertProps['variant'];
  /** Alert sur toute la largeur du Snackbar. */
  fullWidth?: boolean;
  /** Message simple du Snackbar, sans Alert (gravité non affichée). */
  plain?: boolean;
}

interface Notification {
  message: string;
  severity: AlertColor;
  options: Required<Pick<NotifyOptions, 'autoHideMs' | 'fullWidth'>> & NotifyOptions;
}

/** Durée et rendu par défaut (ceux du Snackbar des écrans argent). */
const DEFAULT_OPTIONS = { autoHideMs: 6000, fullWidth: true };

const BOTTOM_CENTER: SnackbarOrigin = { vertical: 'bottom', horizontal: 'center' };
const BOTTOM_RIGHT: SnackbarOrigin = { vertical: 'bottom', horizontal: 'right' };

/** Vérifie chaque préréglage en gardant leurs noms comme type. */
const definePresets = <Name extends string>(presets: Record<Name, NotifyOptions>) => presets;

/**
 * Préréglages de rendu, un par famille d'écrans. Les rendus propres à un
 * seul écran sont conservés tels quels (le lot ne change aucun rendu) ;
 * les unifier est une décision d'interface.
 */
export const NOTIFY_PRESETS = definePresets({
  /** Contenu pédagogique, publicité : Alert compacte, 6 s. */
  content: { autoHideMs: 6000, fullWidth: false },
  /** Campagnes, vidéos à la une, notifications : Alert compacte, 5 s. */
  marketing: { autoHideMs: 5000, fullWidth: false },
  /** Agents IA : Alert compacte centrée, 5 s. */
  agents: { autoHideMs: 5000, anchorOrigin: BOTTOM_CENTER, fullWidth: false },
  /** Utilisateurs, codes d'invitation, collections corrigeables : en bas à droite, 6 s. */
  admin: { autoHideMs: 6000, anchorOrigin: BOTTOM_RIGHT },
  /** Opérations d'argent : 4 s. */
  money: { autoHideMs: 4000 },
  /** Message simple, sans Alert (vignettes). */
  plain: { plain: true },
  /** Revenus : message simple, 5 s. */
  revenus: { plain: true, autoHideMs: 5000 },
  /** Copie d'un code d'invitation : message simple en bas à droite, 2 s. */
  invitationCopy: { plain: true, autoHideMs: 2000, anchorOrigin: BOTTOM_RIGHT },
  /** Mot de passe temporaire : message simple centré, 3 s. */
  temporaryPassword: { plain: true, autoHideMs: 3000, anchorOrigin: BOTTOM_CENTER },
  /** Éditeur d'exercice : centré, 4 s. */
  exerciseEditor: { autoHideMs: 4000, anchorOrigin: BOTTOM_CENTER },
  /** Tableau de bord des agents : Alert compacte en bas à droite, 4 s. */
  agentDashboard: { autoHideMs: 4000, anchorOrigin: BOTTOM_RIGHT, fullWidth: false },
  /** Configuration des crédits IA : Alert pleine compacte en bas à droite, 4 s. */
  creditConfig: { autoHideMs: 4000, anchorOrigin: BOTTOM_RIGHT, variant: 'filled', fullWidth: false },
  /** Rapports de plantage : Alert compacte, 4 s. */
  crashReports: { autoHideMs: 4000, fullWidth: false },
});

/** Nom d'un préréglage de rendu. */
export type NotifyPreset = keyof typeof NOTIFY_PRESETS;

type Show = (message: string, severity: AlertColor, options?: NotifyOptions) => void;

const NotificationContext = createContext<Show | null>(null);

/** Monte le Snackbar commun et expose `useNotify`. */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<Notification | null>(null);

  const show = useCallback<Show>((message, severity, options) => {
    setNotification({ message, severity, options: { ...DEFAULT_OPTIONS, ...options } });
  }, []);

  const close = () => setNotification(null);
  const options = notification?.options ?? DEFAULT_OPTIONS;

  return (
    <NotificationContext.Provider value={show}>
      {children}
      {notification?.options.plain ? (
        <Snackbar open autoHideDuration={options.autoHideMs} onClose={close}
          anchorOrigin={notification.options.anchorOrigin} message={notification.message} />
      ) : (
        <Snackbar open={!!notification} autoHideDuration={options.autoHideMs} onClose={close}
          anchorOrigin={notification?.options.anchorOrigin}>
          <Alert onClose={close} severity={notification?.severity ?? 'error'}
            variant={notification?.options.variant} sx={options.fullWidth ? { width: '100%' } : undefined}>
            {notification?.message}
          </Alert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
};

export interface Notifier {
  /** Affiche un message avec sa gravité. */
  notify: (message: string, severity: AlertColor) => void;
  /** Affiche l'erreur d'un appel API (message du backend, sinon `fallback`). */
  notifyError: (error: unknown, fallback: string) => void;
}

/**
 * Accès au retour commun ; à appeler sous `NotificationProvider`.
 * `preset` fixe le rendu des messages de l'écran (sans préréglage : rendu
 * par défaut).
 */
export const useNotify = (preset?: NotifyPreset): Notifier => {
  const show = useContext(NotificationContext);
  const options: NotifyOptions | undefined = preset && NOTIFY_PRESETS[preset];
  const notifier = useMemo<Notifier | null>(() => show && {
    notify: (message, severity) => show(message, severity, options),
    notifyError: (error, fallback) => {
      logger.error('[Notification][notifyError]', fallback, error);
      show(toUserMessage(error, fallback), 'error', options);
    },
  }, [show, options]);
  if (!notifier) {
    throw new Error('[NotificationContext] useNotify appelé hors de NotificationProvider');
  }
  return notifier;
};
